import { useState, useRef } from "react";
import { X, Upload } from "lucide-react";

export function FileBox({ onClose }) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    if (!files) return;

    const newFiles = Array.from(files).map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      progress: 0,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Simulate upload progress
    newFiles.forEach((file) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === file.id ? { ...f, progress: Math.min(progress, 100) } : f
          )
        );
        if (progress >= 100) {
          clearInterval(interval);
        }
      }, 200);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="file-box">
      {/* Header */}
      <div className="file-box-header">
        <div>
          <h2>Upload and attach files</h2>
          <p className="file-box-subtitle">
            Add files to this project by uploading or dragging and dropping them here.
          </p>
        </div>
        <button
          onClick={onClose}
          className="close-button"
        >
          <X className="icon" />
        </button>
      </div>

      {/* Upload Area */}
      <div
        className={`upload-area ${isDragging ? 'drag-active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="upload-content">
          <div className="upload-icon">
            <Upload className="icon-large" />
            <div className="upload-icon-badge">
              <Upload className="icon-small" />
            </div>
          </div>
          <p className="upload-text-main">
            Click to upload or drag and drop
          </p>
          <p className="upload-text-sub">
            PDF, CSV, XLS, and XLSX files are supported.
          </p>
          <p className="upload-text-sub">Maximum file size: 50 MB</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.csv,.xls,.xlsx"
          className="file-input"
          onChange={(e) => handleFileSelect(e.target.files)}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="file-input-button"
        />
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="files-list">
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className="file-item"
            >
              <div className="file-info">
                <div className="file-details">
                  <p className="file-name">{file.name}</p>
                  <p className="file-size">{formatFileSize(file.size)}</p>
                </div>
                <button
                  onClick={() => handleRemoveFile(file.id)}
                  className="remove-file-button"
                >
                  <X className="icon-small" />
                </button>
              </div>
              {/* Progress Bar */}
              <div className="progress-container">
                <div className="progress-wrapper">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {file.progress}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}